import { z } from 'zod';
import { skinAssetDefects, skinAssetMetadataSchema } from './asset';
import { GENERATED_SKIN_MANIFEST } from './manifest.generated';
import { skinVfxDescriptorSchema } from './vfx';

/**
 * The one record that says a skin is finished (ADR 0041).
 *
 * ## What a manifest entry is for
 *
 * Three things have to agree before a skin may be sold: the catalogue
 * definition, the PNG and its metadata, and the VFX descriptor. Each is
 * authored separately and each is valid on its own, so the interesting failure
 * is not "one of them is malformed" - it is "they do not describe the same
 * skin". That is what this file checks, and it is the only place that does.
 *
 * ## Status is not a wish
 *
 * `status: 'ready'` is a claim, and `skinReadiness` is what tests it. A skin
 * whose entry says ready but whose hash does not match the file, or whose VFX
 * names a different definition, is reported as BLOCKED with the reasons listed
 * - it is never quietly promoted, and it can never reach a banner.
 *
 * ## The template is not content
 *
 * The example the owner works from lives under a reserved id prefix and is
 * refused by `skinReadiness` whatever else it says. An example that could
 * accidentally ship is a worked example of how to ship something by accident.
 */

/** Bumped when this contract changes in a way old manifests cannot satisfy. */
export const SKIN_MANIFEST_VERSION = 1;

/**
 * The reserved prefix for authoring examples.
 *
 * Anything whose definition id starts with this is documentation. It may sit in
 * `assets-source/skins` and be validated like real content - which is the whole
 * point of an example - and it can never be enabled, never enter a pool and
 * never be granted.
 */
export const SKIN_TEMPLATE_PREFIX = 'example_';

export const SKIN_ENTRY_STATUSES = [
  /** Being authored. Never enabled, never in a banner. */
  'draft',
  /** Waiting for the owner to look at captures. Never enabled. */
  'review',
  /** Everything agrees. May be enabled and may enter a pool. */
  'ready',
  /** Withdrawn content. Kept so old instances stay readable. */
  'retired',
] as const;
export type SkinEntryStatus = (typeof SKIN_ENTRY_STATUSES)[number];

export const skinManifestEntrySchema = z
  .object({
    definitionId: z.string().min(3).max(64),
    status: z.enum(SKIN_ENTRY_STATUSES),
    /** Bumped whenever the art or either descriptor changes. */
    contentVersion: z.int().min(1),
    asset: skinAssetMetadataSchema,
    vfx: skinVfxDescriptorSchema,
    /**
     * The format versions this entry was authored against.
     *
     * Recorded rather than inferred so an entry written for an older contract
     * is refused loudly instead of being read with today's assumptions.
     */
    compatibility: z
      .object({
        assetFormatVersion: z.int().min(1),
        vfxFormatVersion: z.int().min(1),
      })
      .strict(),
    note: z.string().max(500).optional(),
  })
  .strict();

export type SkinManifestEntry = z.infer<typeof skinManifestEntrySchema>;

export const skinManifestSchema = z
  .object({
    manifestVersion: z.literal(SKIN_MANIFEST_VERSION),
    entries: z.array(skinManifestEntrySchema),
  })
  .strict()
  .superRefine((manifest, ctx) => {
    const seenDefinitions = new Set<string>();
    const seenAssetKeys = new Set<string>();
    const seenFiles = new Set<string>();
    for (const [index, entry] of manifest.entries.entries()) {
      if (seenDefinitions.has(entry.definitionId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['entries', index, 'definitionId'],
          message: `duplicate definition "${entry.definitionId}"`,
        });
      }
      if (seenAssetKeys.has(entry.asset.assetKey)) {
        ctx.addIssue({
          code: 'custom',
          path: ['entries', index, 'asset', 'assetKey'],
          message: `duplicate assetKey "${entry.asset.assetKey}"`,
        });
      }
      if (seenFiles.has(entry.asset.file)) {
        ctx.addIssue({
          code: 'custom',
          path: ['entries', index, 'asset', 'file'],
          message: `two skins claim the same file "${entry.asset.file}"`,
        });
      }
      seenDefinitions.add(entry.definitionId);
      seenAssetKeys.add(entry.asset.assetKey);
      seenFiles.add(entry.asset.file);
    }
  });

export type SkinManifest = z.infer<typeof skinManifestSchema>;

/* -------------------------------------------------------------------------- */
/*  Readiness                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Every reason a skin may not be sold.
 *
 * A closed vocabulary, so the validator, the banner and the tests all name the
 * same failure. Each one is a SITUATION an author can act on.
 */
export const SKIN_BLOCK_REASONS = [
  /** The entry itself does not claim to be finished. */
  'status_not_ready',
  /** It is the authoring example. */
  'template_entry',
  /** No catalogue definition with that id. */
  'definition_missing',
  /** The definition exists but is not a skin. */
  'definition_not_a_skin',
  /** The catalogue has it disabled. */
  'definition_disabled',
  /** The catalogue's `assetKey` and the manifest's disagree. */
  'asset_key_mismatch',
  /** The VFX descriptor names a different definition. */
  'vfx_definition_mismatch',
  /** The entry was authored against a different contract version. */
  'incompatible_format',
  /** The asset metadata is internally inconsistent. */
  'asset_metadata_invalid',
  /** The file is absent, unreadable, or not a PNG. */
  'asset_file_missing',
  /** The file's bytes do not hash to the declared value. */
  'asset_hash_mismatch',
  /** The file's real dimensions or alpha do not match the declaration. */
  'asset_file_mismatch',
  /** No hunt profile draws anything, or it is unresolvable. */
  'hunt_profile_invalid',
  /** A PvP profile is unresolvable. */
  'pvp_profile_invalid',
  /** The declared fallback is not one of the three levels. */
  'fallback_invalid',
] as const;
export type SkinBlockReason = (typeof SKIN_BLOCK_REASONS)[number];

/** What the catalogue says, handed in rather than imported. */
export interface SkinCatalogueView {
  readonly exists: boolean;
  readonly category: string;
  readonly enabled: boolean;
  readonly assetKey: string | null;
}

/** What the filesystem said, when somebody looked. */
export interface SkinFileVerdict {
  readonly exists: boolean;
  readonly sha256: string | null;
  readonly width: number | null;
  readonly height: number | null;
  readonly hasAlpha: boolean | null;
}

export interface SkinReadiness {
  readonly definitionId: string;
  readonly ready: boolean;
  readonly blockedBy: readonly SkinBlockReason[];
}

/**
 * Whether one skin may be enabled and sold, and why not.
 *
 * Every input is a PARAMETER. The catalogue and the filesystem are both handed
 * in rather than imported, which is what keeps this function pure, testable
 * without a disk, and usable from the API - which has a catalogue but no
 * `assets-source` - and from the validator, which has both.
 *
 * `file` is optional because the API cannot look at the disk. When it is
 * absent, the file-level reasons simply cannot be checked here; the API instead
 * requires an entry the VALIDATOR already marked ready, which is where those
 * checks were made.
 */
export function skinReadiness(input: {
  readonly entry: SkinManifestEntry;
  readonly catalogue: SkinCatalogueView;
  readonly file?: SkinFileVerdict;
}): SkinReadiness {
  const { entry, catalogue, file } = input;
  const blocked: SkinBlockReason[] = [];

  if (entry.definitionId.startsWith(SKIN_TEMPLATE_PREFIX)) {
    blocked.push('template_entry');
  }
  if (entry.status !== 'ready') {
    blocked.push('status_not_ready');
  }
  if (
    entry.compatibility.assetFormatVersion !== entry.asset.formatVersion ||
    entry.compatibility.vfxFormatVersion !== entry.vfx.formatVersion
  ) {
    blocked.push('incompatible_format');
  }

  if (!catalogue.exists) {
    blocked.push('definition_missing');
  } else {
    if (catalogue.category !== 'skin') {
      blocked.push('definition_not_a_skin');
    }
    if (!catalogue.enabled) {
      blocked.push('definition_disabled');
    }
    if (catalogue.assetKey !== entry.asset.assetKey) {
      blocked.push('asset_key_mismatch');
    }
  }

  if (entry.vfx.definitionId !== entry.definitionId) {
    blocked.push('vfx_definition_mismatch');
  }
  if (skinAssetDefects(entry.asset).length > 0) {
    blocked.push('asset_metadata_invalid');
  }

  /*
   * A mode that draws nothing is not a cheaper skin, it is an absent one.
   *
   * The overlay and the trail are mandatory and are never in `signatures`, so
   * what this actually checks is that the mode has not scaled them into
   * nothing - which the schema permits per field but which is meaningless as a
   * combination.
   */
  if (entry.vfx.hunt.frequencyScale <= 0 || entry.vfx.hunt.particleScale <= 0) {
    blocked.push('hunt_profile_invalid');
  }
  if (
    entry.vfx.pvp1x1.frequencyScale <= 0 ||
    entry.vfx.pvp1x1.particleScale <= 0 ||
    entry.vfx.pvp3x3.frequencyScale <= 0 ||
    entry.vfx.pvp3x3.particleScale <= 0
  ) {
    blocked.push('pvp_profile_invalid');
  }
  if (!['full', 'reduced', 'minimal'].includes(entry.vfx.fallbackLod)) {
    blocked.push('fallback_invalid');
  }

  if (file) {
    if (!file.exists) {
      blocked.push('asset_file_missing');
    } else {
      if (file.sha256 !== entry.asset.sha256) {
        blocked.push('asset_hash_mismatch');
      }
      if (
        file.width !== entry.asset.width ||
        file.height !== entry.asset.height ||
        file.hasAlpha !== true
      ) {
        blocked.push('asset_file_mismatch');
      }
    }
  }

  const blockedBy = [...new Set(blocked)];
  return { definitionId: entry.definitionId, ready: blockedBy.length === 0, blockedBy };
}

/** The entries that may legitimately appear in a Cash banner's pool. */
export function readySkins(
  manifest: SkinManifest,
  resolve: (definitionId: string) => SkinCatalogueView,
  file?: (entry: SkinManifestEntry) => SkinFileVerdict,
): readonly SkinManifestEntry[] {
  return manifest.entries.filter((entry) => {
    const verdict = skinReadiness({
      entry,
      catalogue: resolve(entry.definitionId),
      ...(file ? { file: file(entry) } : {}),
    });
    return verdict.ready;
  });
}

/**
 * The manifest this build ships. **A projection, never authored by hand.**
 *
 * ## Where the numbers actually live
 *
 * In `assets-source/skins/<id>/skin.json`, which the pipeline validates and
 * which remains the only place a pivot, a hash, a particle count or a file name
 * is written. `manifest.generated.ts` is produced from those descriptors by
 * `pnpm assets:prepare`, and `manifest.generated.test.ts` fails if the file in
 * the repository is not what a regeneration would produce.
 *
 * This constant was an empty array until the Closed Alpha activation, with a
 * comment saying no skin had a PNG. Filling it in by hand would have created
 * exactly the second source of truth an earlier gate spent itself removing: the
 * server's copy of the geometry, free to drift from the art the validator
 * checks.
 *
 * ## What is deliberately absent
 *
 * Drafts, at every stage: the generator has no `includeDraft` mode, because
 * this is the catalogue that decides whether a skin may be SOLD. The authoring
 * example is absent too - it lives under `assets-source/skins/` as a file the
 * validator reads, so it can be checked and copied from without ever being a
 * thing the catalogue knows about.
 */
export const SKIN_MANIFEST: SkinManifest = Object.freeze({
  manifestVersion: SKIN_MANIFEST_VERSION,
  entries: GENERATED_SKIN_MANIFEST.entries as unknown as SkinManifestEntry[],
});
